import { useEffect, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { api, API, extractGenreLabel } from "../../lib/api";
import { StatsCleanup } from "./StatsCleanup";

const opts = { withCredentials: true };
const PERIODS = [{ v: 7, l: "7 jours" }, { v: 30, l: "30 jours" }, { v: 90, l: "90 jours" }, { v: 0, l: "Tout" }];
const pct = (r) => (r === null || r === undefined ? "—" : `${Math.round(r * 100)} %`);
const mins = (s) => (s >= 60 ? `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, "0")}` : `${Math.round(s)} s`);
const BUCKETS = [["lt2", "< 2 min"], ["2to4", "2 – 4 min"], ["4to6", "4 – 6 min"], ["gt6", "> 6 min"]];

const Kpi = ({ label, value, hint, testid }) => (
  <div data-testid={testid} className="border border-[#D1CFC7] p-5">
    <p className="text-[10px] tracking-[0.2em] uppercase text-[#757575] font-body mb-2">{label}</p>
    <p className="font-serif text-4xl text-[#1A1A1A] font-light">{value}</p>
    {hint && <p className="text-[11px] font-body text-[#757575] mt-1">{hint}</p>}
  </div>
);

const Durations = ({ buckets, total }) => (
  <div data-testid="stats-durations" className="border border-[#D1CFC7] p-5">
    <p className="text-[10px] tracking-[0.2em] uppercase text-[#757575] font-body mb-4">Temps passé sur l'histoire</p>
    <div className="space-y-3">
      {BUCKETS.map(([k, l]) => (
        <div key={k} className="flex items-center gap-3 text-xs font-body">
          <span className="w-20 text-[#4A4A4A]">{l}</span>
          <div className="flex-1 h-3 bg-[#F4F1EA]"><div className="h-full bg-[#2C402E] transition-[width] duration-500" style={{ width: total ? `${(buckets[k] / total) * 100}%` : 0 }} /></div>
          <span className="w-16 text-right text-[#1A1A1A]">{buckets[k]} ({total ? Math.round((buckets[k] / total) * 100) : 0} %)</span>
        </div>
      ))}
    </div>
  </div>
);

const COLS = [
  ["Visites", (r) => r.visits], ["Temps moyen", (r) => mins(r.avg_duration_sec)], ["Lecture complète", (r) => pct(r.completion_rate)],
  ["Clic bouton fin", (r) => pct(r.cta_rate)], ["WhatsApp", (r) => r.whatsapp_clicks], ["Boutique / catalogue", (r) => r.secondary_clicks + r.catalog_clicks],
  ["Offre partenaire", (r) => r.partner_offer_clicks], ["Satisfaction", (r) => `${pct(r.csat_rate)} (${r.csat_responses})`],
  ["Image partenaire +", (r) => `${pct(r.partner_positive_rate)} (${r.partner_responses})`], ["Prospects", (r) => r.leads],
];

const Table = ({ title, rows, label, testid }) => (
  <section data-testid={testid} className="mt-10">
    <h3 className="font-serif text-2xl text-[#1A1A1A] mb-4">{title}</h3>
    <div className="overflow-x-auto border border-[#D1CFC7]">
      <table className="w-full text-xs font-body whitespace-nowrap">
        <thead className="bg-[#F4F1EA] text-left uppercase tracking-wider text-[#4A4A4A]">
          <tr><th className="p-3">{label[0]}</th>{COLS.map(([h]) => <th key={h} className="p-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[#E8E4D9]">
              <td className="p-3 text-[#1A1A1A] text-sm">{label[1](r)}</td>
              {COLS.map(([h, f]) => <td key={h} className="p-3 text-[#4A4A4A]">{f(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export function ExtractStatsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/admin/extract-stats", { ...opts, params: days ? { days } : {} }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days, tick]);

  return (
    <div data-testid="extract-stats-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Statistiques des histoires</h2>
        <div className="flex border border-[#D1CFC7]">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setDays(p.v)} data-testid={`stats-period-${p.v}`} className={`px-3 py-1.5 text-xs font-body transition-colors ${days === p.v ? "bg-[#1A1A1A] text-[#FAF9F6]" : "text-[#4A4A4A] hover:bg-[#F4F1EA]"}`}>{p.l}</button>
          ))}
        </div>
        <div className="basis-full flex flex-wrap items-start gap-3">
          <a href={`${API}/admin/extract-stats/export${days ? `?days=${days}` : ""}`} data-testid="stats-export-excel" className="flex items-center gap-2 border border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-[#FAF9F6] px-3 py-1.5 text-xs font-body transition-colors"><FileSpreadsheet size={13} /> Exporter en Excel (par histoire, par jour, détail)</a>
          <StatsCleanup onDone={() => setTick((t) => t + 1)} />
        </div>
      </div>
      {!data ? <p className="font-body text-sm text-[#757575]">Chargement…</p> : <StatsBody data={data} />}
    </div>
  );
}

const StatsBody = ({ data }) => {
  const g = data.global;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-visits" label="Visites (scans)" value={g.visits} />
        <Kpi testid="kpi-avg-time" label="Temps moyen" value={mins(g.avg_duration_sec)} />
        <Kpi testid="kpi-completion" label="Lecture complète" value={pct(g.completion_rate)} hint="Lecteurs arrivés à « Fin de l'histoire »" />
        <Kpi testid="kpi-cta" label="Clic bouton de fin" value={pct(g.cta_rate)} hint="Livre complet, boutique ou offre partenaire" />
        <Kpi testid="kpi-redirects" label="Redirections ventes" value={g.sales_redirects} hint={`WhatsApp ${g.whatsapp_clicks} · Boutique/catalogue ${g.secondary_clicks + g.catalog_clicks}`} />
        <Kpi testid="kpi-csat" label="Satisfaction (CSAT)" value={pct(g.csat_rate)} hint={`${g.csat_responses} réponse(s) : moment plus agréable`} />
        <Kpi testid="kpi-partner" label="Image partenaire +" value={pct(g.partner_positive_rate)} hint={`${g.partner_responses} réponse(s)`} />
        <Kpi testid="kpi-leads" label="Prospects newsletter" value={g.leads} />
      </div>
      <div className="mt-4"><Durations buckets={g.duration_buckets} total={g.visits} /></div>
      <Table testid="stats-per-lieu" title="Par partenaire" rows={data.per_lieu} label={["Partenaire", (r) => <>{r.name}{r.city ? <span className="text-[#757575]"> — {r.city}</span> : null}{r.qr_scans !== null && r.qr_scans !== undefined && <span className="block text-[11px] text-[#9E4735]">{r.qr_scans} scan(s) du QR code</span>}<span className="block text-[11px] text-[#757575] max-w-[260px] truncate">{r.extract_title}</span></>]} />
      <Table testid="stats-per-extract" title="Par histoire" rows={data.per_extract} label={["Histoire", (r) => r.title]} />
      <Table testid="stats-by-format" title="Préférences : durée de lecture" rows={data.by_format} label={["Format", (r) => (r.key === "non-renseigne" ? "Non renseigné" : r.key)]} />
      <Table testid="stats-by-genre" title="Préférences : genre" rows={data.by_genre} label={["Genre", (r) => extractGenreLabel(r.key)]} />
    </>
  );
};
